// ====================================================================
// CLIENT FOLLOW-UP CRM - CLIENT MANAGEMENT VIEW (CRUD & FILTERS)
// ====================================================================

import { getClients, getUsers, addClient, updateClient, deleteClient } from '../store.js';
import { isAdmin, getCurrentUser } from '../auth.js';
import { showToast, openModal, closeModal, escapeHtml } from '../ui.js';
import { openAddInteractionModal } from './staff_view.js';

let allClientsCache = [];
let allUsersCache = [];

export async function renderClientsView(container) {
  allClientsCache = await getClients();
  allUsersCache = await getUsers();

  const isUserAdmin = isAdmin();

  container.innerHTML = `
    <div class="page-header">
      <div>
        <h1 class="page-title">Client Directory</h1>
        <p class="page-subtitle">Manage client accounts, categories, and assigned owners</p>
      </div>
      <button id="btn-add-client-main" class="btn btn-primary">
        <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/></svg>
        Add Client
      </button>
    </div>

    <!-- Filters Panel -->
    <div class="card" style="margin-bottom: 1.5rem;">
      <div class="form-row">
        <div class="form-group" style="margin-bottom:0;">
          <label class="form-label">Search Name / Mobile</label>
          <input type="text" id="filter-search" class="form-control" placeholder="Search by name or phone...">
        </div>

        <div class="form-group" style="margin-bottom:0;">
          <label class="form-label">Category</label>
          <select id="filter-category" class="form-select">
            <option value="">All Categories</option>
            <option value="Hot">Hot</option>
            <option value="Warm">Warm</option>
            <option value="Cold">Cold</option>
            <option value="Closed">Closed</option>
          </select>
        </div>

        <div class="form-group" style="margin-bottom:0;">
          <label class="form-label">Owner</label>
          <select id="filter-owner" class="form-select">
            <option value="">All Owners</option>
            ${allUsersCache.map(u => `<option value="${u.id}">${escapeHtml(u.name)}</option>`).join('')}
          </select>
        </div>
      </div>
    </div>

    <!-- Clients Table -->
    <div class="card">
      <div class="table-responsive">
        <table class="table">
          <thead>
            <tr>
              <th>Client Name</th>
              <th>Mobile Number</th>
              <th>Company</th>
              <th>Category</th>
              <th>Owner</th>
              <th style="text-align: right;">Actions</th>
            </tr>
          </thead>
          <tbody id="clients-tbody">
            <!-- Dynamic rows -->
          </tbody>
        </table>
      </div>
    </div>
  `;

  renderClientRows(allClientsCache);

  // Attach filter listeners
  const filterSearch = document.getElementById('filter-search');
  const filterCategory = document.getElementById('filter-category');
  const filterOwner = document.getElementById('filter-owner');

  const applyFilters = () => {
    const s = filterSearch.value.trim().toLowerCase();
    const cat = filterCategory.value;
    const own = filterOwner.value;

    const filtered = allClientsCache.filter(c => {
      const matchSearch = !s || c.client_name.toLowerCase().includes(s) || (c.mobile_number && c.mobile_number.includes(s)) || (c.company && c.company.toLowerCase().includes(s));
      const matchCat = !cat || c.category === cat;
      const matchOwn = !own || c.owner_id === own;
      return matchSearch && matchCat && matchOwn;
    });

    renderClientRows(filtered);
  };

  filterSearch.addEventListener('input', applyFilters);
  filterCategory.addEventListener('change', applyFilters);
  filterOwner.addEventListener('change', applyFilters);

  document.getElementById('btn-add-client-main').addEventListener('click', () => {
    openClientModal();
  });
}

function renderClientRows(clients) {
  const tbody = document.getElementById('clients-tbody');
  const isUserAdmin = isAdmin();

  if (clients.length === 0) {
    tbody.innerHTML = `<tr><td colspan="6" style="text-align:center; color:var(--text-muted); padding:2rem;">No matching clients found.</td></tr>`;
    return;
  }

  tbody.innerHTML = clients.map(c => `
    <tr>
      <td>
        <a href="#profile?id=${c.id}" style="color: var(--primary); text-decoration:none; font-weight:700;">
          ${escapeHtml(c.client_name)}
        </a>
      </td>
      <td><strong>${escapeHtml(c.mobile_number)}</strong> ${c.alternate_mobile ? `<br><span style="font-size:0.75rem; color:var(--text-muted);">Alt: ${escapeHtml(c.alternate_mobile)}</span>` : ''}</td>
      <td>${escapeHtml(c.company || '-')}</td>
      <td><span class="badge badge-${(c.category || 'warm').toLowerCase()}">${escapeHtml(c.category || 'Warm')}</span></td>
      <td>${escapeHtml(c.owner?.name || 'Unassigned')}</td>
      <td style="text-align: right;">
        <div style="display:inline-flex; gap:0.35rem;">
          <button class="btn btn-secondary btn-sm btn-action-int" data-id="${c.id}" data-name="${escapeHtml(c.client_name)}">+ Interaction</button>
          <button class="btn btn-secondary btn-sm btn-action-edit" data-id="${c.id}">Edit</button>
          ${isUserAdmin ? `<button class="btn btn-danger btn-sm btn-action-del" data-id="${c.id}" data-name="${escapeHtml(c.client_name)}">Delete</button>` : ''}
        </div>
      </td>
    </tr>
  `).join('');

  // Attach event handlers
  document.querySelectorAll('.btn-action-int').forEach(b => {
    b.addEventListener('click', (e) => {
      openAddInteractionModal(e.target.dataset.id, e.target.dataset.name);
    });
  });

  document.querySelectorAll('.btn-action-edit').forEach(b => {
    b.addEventListener('click', (e) => {
      const client = allClientsCache.find(c => c.id === e.target.dataset.id);
      if (client) openClientModal(client);
    });
  });

  if (isUserAdmin) {
    document.querySelectorAll('.btn-action-del').forEach(b => {
      b.addEventListener('click', async (e) => {
        const id = e.target.dataset.id;
        const name = e.target.dataset.name;
        if (confirm(`Are you sure you want to delete client "${name}"? This action cannot be undone.`)) {
          try {
            await deleteClient(id);
            showToast(`Client "${name}" deleted.`, 'success');
            allClientsCache = await getClients();
            renderClientRows(allClientsCache);
          } catch (err) {
            showToast(err.message || 'Failed to delete client', 'error');
          }
        }
      });
    });
  }
}

export function openClientModal(client = null) {
  const modalBody = document.getElementById('modal-client-body');
  document.getElementById('modal-client-title').textContent = client ? 'Edit Client Details' : 'Add New Client';

  const currentUser = getCurrentUser();

  modalBody.innerHTML = `
    <form id="client-crud-form">
      <div class="form-row">
        <div class="form-group">
          <label class="form-label">Client Name <span class="required">*</span></label>
          <input type="text" id="crud-name" class="form-control" required value="${escapeHtml(client?.client_name || '')}">
        </div>

        <div class="form-group">
          <label class="form-label">Mobile Number <span class="required">*</span></label>
          <input type="tel" id="crud-mobile" class="form-control" required value="${escapeHtml(client?.mobile_number || '')}">
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label class="form-label">Alternate Mobile</label>
          <input type="tel" id="crud-alt-mobile" class="form-control" value="${escapeHtml(client?.alternate_mobile || '')}">
        </div>

        <div class="form-group">
          <label class="form-label">Email Address</label>
          <input type="email" id="crud-email" class="form-control" value="${escapeHtml(client?.email || '')}">
        </div>
      </div>

      <div class="form-row">
        <div class="form-group">
          <label class="form-label">Company</label>
          <input type="text" id="crud-company" class="form-control" value="${escapeHtml(client?.company || '')}">
        </div>

        <div class="form-group">
          <label class="form-label">Category <span class="required">*</span></label>
          <select id="crud-category" class="form-select" required>
            <option value="Hot" ${client?.category === 'Hot' ? 'selected' : ''}>Hot Lead</option>
            <option value="Warm" ${client?.category === 'Warm' || !client ? 'selected' : ''}>Warm Lead</option>
            <option value="Cold" ${client?.category === 'Cold' ? 'selected' : ''}>Cold Lead</option>
            <option value="Closed" ${client?.category === 'Closed' ? 'selected' : ''}>Closed</option>
          </select>
        </div>
      </div>

      <div class="form-group">
        <label class="form-label">Assigned Owner</label>
        <select id="crud-owner" class="form-select">
          ${allUsersCache.map(u => `
            <option value="${u.id}" ${client?.owner_id === u.id || (!client && currentUser?.id === u.id) ? 'selected' : ''}>
              ${escapeHtml(u.name)} (${escapeHtml(u.role)})
            </option>
          `).join('')}
        </select>
      </div>

      <div class="form-group">
        <label class="form-label">Address</label>
        <textarea id="crud-address" class="form-control" rows="2">${escapeHtml(client?.address || '')}</textarea>
      </div>

      <div class="modal-footer" style="padding: 1rem 0 0 0; margin-top: 1rem;">
        <button type="button" class="btn btn-secondary" onclick="document.getElementById('modal-client').classList.remove('active')">Cancel</button>
        <button type="submit" class="btn btn-primary">${client ? 'Update Client' : 'Save Client'}</button>
      </div>
    </form>
  `;

  openModal('modal-client');

  document.getElementById('client-crud-form').addEventListener('submit', async (e) => {
    e.preventDefault();

    const payload = {
      client_name: document.getElementById('crud-name').value.trim(),
      mobile_number: document.getElementById('crud-mobile').value.trim(),
      alternate_mobile: document.getElementById('crud-alt-mobile').value.trim(),
      email: document.getElementById('crud-email').value.trim(),
      company: document.getElementById('crud-company').value.trim(),
      category: document.getElementById('crud-category').value,
      owner_id: document.getElementById('crud-owner').value || null,
      address: document.getElementById('crud-address').value.trim()
    };

    try {
      if (client) {
        await updateClient(client.id, payload);
        showToast('Client updated successfully!', 'success');
      } else {
        await addClient(payload);
        showToast('Client created successfully!', 'success');
      }
      closeModal('modal-client');
      allClientsCache = await getClients();
      renderClientRows(allClientsCache);
    } catch (err) {
      showToast(err.message || 'Error saving client', 'error');
    }
  });
}
